import bs58 from "bs58";
import { WalletMissingError } from "./errors";
import type { PhantomSolanaProvider } from "./global";

export const PHANTOM_INSTALL_URL = "https://phantom.app/download";

export function getPhantom(): PhantomSolanaProvider | null {
  if (typeof window === "undefined") return null;
  const injected = window.phantom?.solana ?? window.solana;
  if (!injected) return null;
  return injected.isPhantom === false ? null : injected;
}

export const hasPhantom = (): boolean => getPhantom() !== null;

function requirePhantom(): PhantomSolanaProvider {
  const provider = getPhantom();
  if (!provider) throw new WalletMissingError("phantom");
  return provider;
}

/** Opens Phantom and hands back the base58 public key of the selected account. */
export async function connectPhantom(): Promise<string> {
  const provider = requirePhantom();
  const { publicKey } = await provider.connect();
  return publicKey.toString();
}

/** Reconnects silently when the site is already trusted, otherwise hands back null. */
export async function connectPhantomIfTrusted(): Promise<string | null> {
  const provider = getPhantom();
  if (!provider) return null;
  try {
    const { publicKey } = await provider.connect({ onlyIfTrusted: true });
    return publicKey.toString();
  } catch {
    return null;
  }
}

/**
 * Signs the fixed template with the connected Solana account and hands back the
 * signature base58 encoded, which is what POST /api/register expects.
 */
export async function signMessageWithPhantom(
  message: string,
  expectedAddress: string,
): Promise<string> {
  const provider = requirePhantom();
  const connected = provider.publicKey?.toString() ?? (await connectPhantom());
  if (connected !== expectedAddress) {
    throw new Error(
      `Phantom is on ${connected}. Switch to ${expectedAddress} in Phantom, then try again.`,
    );
  }
  const encoded = new TextEncoder().encode(message);
  // Phantom's injected provider hands back signature bytes; its Browser SDK hands back a base58
  // string with no public key. Accept both so a wallet update cannot silently break signing.
  const signed = await provider.signMessage(encoded, "utf8");
  return typeof signed.signature === "string" ? signed.signature : bs58.encode(signed.signature);
}
